import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "../features/auth/AuthProvider";
import { LoginPage } from "../features/auth/LoginPage";
import { RoleGate } from "../features/auth/RoleGate";
import { PanelLayout } from "../layouts/PanelLayout";

const AdminDashboard = lazy(() =>
  import("../features/dashboard/AdminDashboard").then((module) => ({
    default: module.AdminDashboard,
  })),
);
const SuperadminDashboard = lazy(() =>
  import("../features/dashboard/SuperadminDashboard").then((module) => ({
    default: module.SuperadminDashboard,
  })),
);
const UsersPage = lazy(() =>
  import("../features/superadmin/users/UsersPage").then((module) => ({
    default: module.UsersPage,
  })),
);
const ParishesPage = lazy(() =>
  import("../features/superadmin/parishes/ParishesPage").then((module) => ({
    default: module.ParishesPage,
  })),
);
const SourcesPage = lazy(() =>
  import("../features/superadmin/sources/SourcesPage").then((module) => ({
    default: module.SourcesPage,
  })),
);
const ChannelsPage = lazy(() =>
  import("../features/superadmin/channels/ChannelsPage").then((module) => ({
    default: module.ChannelsPage,
  })),
);
const StartupMessagesPage = lazy(() =>
  import("../features/superadmin/application/StartupMessagesPage").then(
    (module) => ({ default: module.StartupMessagesPage }),
  ),
);
const LegalDocumentsPage = lazy(() =>
  import("../features/superadmin/application/LegalDocumentsPage").then(
    (module) => ({ default: module.LegalDocumentsPage }),
  ),
);
const PreTransmissionPage = lazy(() =>
  import("../features/superadmin/application/PreTransmissionPage").then(
    (module) => ({ default: module.PreTransmissionPage }),
  ),
);
const ChurchesPage = lazy(() =>
  import("../features/admin/churches/ChurchesPage").then((module) => ({
    default: module.ChurchesPage,
  })),
);
const SupportPage = lazy(() =>
  import("../features/admin/support/SupportPage").then((module) => ({
    default: module.SupportPage,
  })),
);
const LivePage = lazy(() =>
  import("../features/admin/live/LivePage").then((module) => ({
    default: module.LivePage,
  })),
);
const YoutubePage = lazy(() =>
  import("../features/admin/youtube/YoutubePage").then((module) => ({
    default: module.YoutubePage,
  })),
);
const NotificationsPage = lazy(() =>
  import("../features/admin/notifications/NotificationsPage").then(
    (module) => ({ default: module.NotificationsPage }),
  ),
);
const ParishUsersPage = lazy(() =>
  import("../features/admin/users/ParishUsersPage").then((module) => ({
    default: module.ParishUsersPage,
  })),
);
const WebsiteOverviewPage = lazy(() =>
  import("../features/admin/website/WebsiteOverviewPage").then((module) => ({
    default: module.WebsiteOverviewPage,
  })),
);
const WebsiteBuilderPage = lazy(() =>
  import("../features/admin/website/WebsiteBuilderPage").then((module) => ({
    default: module.WebsiteBuilderPage,
  })),
);
const WebsiteAppearancePage = lazy(() =>
  import("../features/admin/website/WebsiteAppearancePage").then((module) => ({
    default: module.WebsiteAppearancePage,
  })),
);
const ParishProfilePage = lazy(() =>
  import("../features/admin/website/ParishProfilePage").then((module) => ({
    default: module.ParishProfilePage,
  })),
);
const MediaLibraryPage = lazy(() =>
  import("../features/admin/website/MediaLibraryPage").then((module) => ({
    default: module.MediaLibraryPage,
  })),
);
const ParishImagesPage = lazy(() =>
  import("../features/admin/website/ParishImagesPage").then((module) => ({
    default: module.ParishImagesPage,
  })),
);
const ModulesMenuPage = lazy(() =>
  import("../features/admin/website/ModulesMenuPage").then((module) => ({
    default: module.ModulesMenuPage,
  })),
);
const CemeteryPage = lazy(() =>
  import("../features/admin/website/CemeteryPage").then((module) => ({
    default: module.CemeteryPage,
  })),
);
const RevisionsPage = lazy(() =>
  import("../features/admin/website/RevisionsPage").then((module) => ({
    default: module.RevisionsPage,
  })),
);
const PagesPage = lazy(() =>
  import("../features/admin/website/ContentPages").then((module) => ({
    default: module.PagesPage,
  })),
);
const PostsPage = lazy(() =>
  import("../features/admin/website/ContentPages").then((module) => ({
    default: module.PostsPage,
  })),
);
const RecordsPage = lazy(() =>
  import("../features/admin/website/ContentPages").then((module) => ({
    default: module.RecordsPage,
  })),
);
const GalleriesPage = lazy(() =>
  import("../features/admin/website/ContentPages").then((module) => ({
    default: module.GalleriesPage,
  })),
);
const MenuLinksPage = lazy(() =>
  import("../features/admin/website/ContentPages").then((module) => ({
    default: module.MenuLinksPage,
  })),
);
const ServerMonitoringPage = lazy(() =>
  import("../features/monitoring/ServerMonitoringPage").then((module) => ({
    default: module.ServerMonitoringPage,
  })),
);
const MediaMtxPage = lazy(() =>
  import("../features/monitoring/MediaMtxPage").then((module) => ({
    default: module.MediaMtxPage,
  })),
);

function HomeRedirect() {
  const auth = useAuth();
  if (auth.status === "loading")
    return (
      <div className="page-loader">
        <span className="spinner-border" /> Ładowanie sesji…
      </div>
    );
  if (!auth.user) return <Navigate to="/login" replace />;
  return (
    <Navigate
      to={auth.user.role === "SUPERADMIN" ? "/superadmin" : "/admin"}
      replace
    />
  );
}

export function App() {
  const basename =
    window.location.pathname === "/next" ||
    window.location.pathname.startsWith("/next/")
      ? "/next"
      : undefined;
  return (
    <BrowserRouter basename={basename}>
      <AuthProvider>
        <Suspense
          fallback={
            <div className="route-loader" role="status">
              <span className="spinner-border" /> Ładowanie widoku…
            </div>
          }
        >
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/superadmin"
              element={
                <RoleGate role="SUPERADMIN">
                  <PanelLayout role="SUPERADMIN" />
                </RoleGate>
              }
            >
              <Route index element={<SuperadminDashboard />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="parishes" element={<ParishesPage />} />
              <Route path="sources" element={<SourcesPage />} />
              <Route path="channels" element={<ChannelsPage />} />
              <Route
                path="application/startup"
                element={<StartupMessagesPage />}
              />
              <Route
                path="application/legal"
                element={<LegalDocumentsPage />}
              />
              <Route
                path="application/pre-transmission"
                element={<PreTransmissionPage />}
              />
              <Route
                path="monitoring/servers"
                element={<ServerMonitoringPage />}
              />
              <Route path="monitoring/mediamtx" element={<MediaMtxPage />} />
              <Route
                path="parishes/:parishId/website"
                element={<WebsiteBuilderPage />}
              />
              <Route path="parishes/:parishId/website/advanced" element={<WebsiteOverviewPage />} />
              <Route
                path="parishes/:parishId/website/appearance"
                element={<WebsiteAppearancePage />}
              />
              <Route
                path="parishes/:parishId/website/profile"
                element={<ParishProfilePage />}
              />
              <Route
                path="parishes/:parishId/website/media"
                element={<MediaLibraryPage />}
              />
              <Route
                path="parishes/:parishId/website/images"
                element={<ParishImagesPage />}
              />
              <Route
                path="parishes/:parishId/website/pages"
                element={<PagesPage />}
              />
              <Route
                path="parishes/:parishId/website/posts"
                element={<PostsPage />}
              />
              <Route
                path="parishes/:parishId/website/records"
                element={<RecordsPage />}
              />
              <Route
                path="parishes/:parishId/website/galleries"
                element={<GalleriesPage />}
              />
              <Route
                path="parishes/:parishId/website/modules"
                element={<ModulesMenuPage />}
              />
              <Route
                path="parishes/:parishId/website/menu"
                element={<MenuLinksPage />}
              />
              <Route
                path="parishes/:parishId/website/cemetery"
                element={<CemeteryPage />}
              />
              <Route
                path="parishes/:parishId/website/revisions"
                element={<RevisionsPage />}
              />
              <Route
                path="parishes/:parishId/churches"
                element={<ChurchesPage />}
              />
              <Route
                path="parishes/:parishId/support"
                element={<SupportPage />}
              />
              <Route path="parishes/:parishId/live" element={<LivePage />} />
              <Route
                path="parishes/:parishId/live/:sourceId"
                element={<LivePage />}
              />
              <Route
                path="parishes/:parishId/youtube"
                element={<YoutubePage />}
              />
              <Route
                path="parishes/:parishId/notifications"
                element={<NotificationsPage />}
              />
              <Route
                path="parishes/:parishId/users"
                element={<ParishUsersPage />}
              />
              <Route path="*" element={<Navigate to="/superadmin" replace />} />
            </Route>
            <Route
              path="/admin"
              element={
                <RoleGate role="ADMIN">
                  <PanelLayout role="ADMIN" />
                </RoleGate>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="website" element={<WebsiteBuilderPage />} />
              <Route path="website/advanced" element={<WebsiteOverviewPage />} />
              <Route
                path="website/appearance"
                element={<WebsiteAppearancePage />}
              />
              <Route path="website/profile" element={<ParishProfilePage />} />
              <Route path="website/media" element={<MediaLibraryPage />} />
              <Route path="website/images" element={<ParishImagesPage />} />
              <Route path="website/pages" element={<PagesPage />} />
              <Route path="website/posts" element={<PostsPage />} />
              <Route path="website/records" element={<RecordsPage />} />
              <Route path="website/galleries" element={<GalleriesPage />} />
              <Route path="website/modules" element={<ModulesMenuPage />} />
              <Route path="website/menu" element={<MenuLinksPage />} />
              <Route path="website/cemetery" element={<CemeteryPage />} />
              <Route path="website/revisions" element={<RevisionsPage />} />
              <Route path="churches" element={<ChurchesPage />} />
              <Route path="support" element={<SupportPage />} />
              <Route path="live" element={<LivePage />} />
              <Route path="live/:sourceId" element={<LivePage />} />
              <Route path="youtube" element={<YoutubePage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="users" element={<ParishUsersPage />} />
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Route>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="*" element={<HomeRedirect />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}
